import type { WorkflowSpec, WorkflowStep } from "~/services/pinnedArtifacts";

export type LineageNodeKind = "source" | "query" | "code" | "intermediate" | "artifact";

export interface LineageNode {
  id: string;
  kind: LineageNodeKind;
  label: string;
  sub: string;
  // The untruncated identity shown when the node is selected: a table's
  // fully qualified name, an artifact's name, or what a step produces.
  full: string;
  // Index into `spec.steps` of the step this node belongs to; null for a
  // source table, which no step produces.
  stepIndex: number | null;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LineageEdge {
  from: string;
  to: string;
  // SVG path from the bottom of `from` to the top of `to`.
  path: string;
}

export interface LineageLayout {
  nodes: LineageNode[];
  edges: LineageEdge[];
  width: number;
  height: number;
}

export const NODE_WIDTH = 176;
const GAP_X = 28;
const GAP_Y = 36;
const BOX_HEIGHT = 64;
const PILL_HEIGHT = 56;

const sourceId = (table: string) => `src:${table}`;
const runId = (stepId: string) => `run:${stepId}`;
const outId = (stepId: string) => `out:${stepId}`;

const lastSegment = (name: string) => name.split(".").pop() ?? name;
const parentPath = (name: string) => name.split(".").slice(0, -1).join(".");

type Unplaced = Omit<LineageNode, "x" | "y" | "width" | "height">;

const processNode = (step: WorkflowStep, index: number): Unplaced => ({
  id: runId(step.step_id),
  kind: step.kind === "sql" ? "query" : "code",
  label: step.summary ?? step.step_id,
  sub: step.kind === "sql" ? "SQL" : (step.runtime ?? "Code"),
  full: `Produces ${step.step_id}`,
  stepIndex: index,
});

const outputNode = (step: WorkflowStep, index: number, isTarget: boolean): Unplaced => ({
  id: outId(step.step_id),
  kind: isTarget ? "artifact" : "intermediate",
  label: step.step_id,
  sub: step.kind === "frozen_input" ? "Fixed input" : step.artifact_type,
  full: step.step_id,
  stepIndex: index,
});

/** Lay a workflow out as a top-to-bottom layered graph.
 *
 * Every SQL or code step becomes two nodes, the work that ran and the artifact
 * it produced, so the drawing alternates data and transformation the way the
 * recipe does. A fixed input has no work to show and is only its artifact.
 * Source tables are drawn once however many queries read them.
 *
 * A node's row is one below its lowest input. Within a row, nodes are ordered
 * by the mean position of their inputs, which keeps parallel chains from
 * crossing in the common cases without a full crossing-minimization pass.
 */
export function layoutWorkflow(spec: WorkflowSpec): LineageLayout {
  const nodes = new Map<string, Unplaced>();
  const preds = new Map<string, string[]>();
  const edges: Array<Pick<LineageEdge, "from" | "to">> = [];
  const connect = (from: string, to: string) => {
    edges.push({ from, to });
    preds.set(to, [...(preds.get(to) ?? []), from]);
  };

  spec.steps.forEach((step, index) => {
    const isTarget = step.step_id === spec.target_step_id;
    const output = outputNode(step, index, isTarget);
    if (step.kind === "frozen_input") {
      nodes.set(output.id, output);
      return;
    }
    for (const table of step.source_tables) {
      if (!nodes.has(sourceId(table))) {
        nodes.set(sourceId(table), {
          id: sourceId(table),
          kind: "source",
          label: lastSegment(table),
          sub: parentPath(table),
          full: table,
          stepIndex: null,
        });
      }
    }
    const run = processNode(step, index);
    nodes.set(run.id, run);
    for (const table of step.source_tables) connect(sourceId(table), run.id);
    for (const input of step.inputs) connect(outId(input), run.id);
    nodes.set(output.id, output);
    connect(run.id, output.id);
  });

  // Steps arrive in dependency order, so one pass sees every input's row
  // before the node that consumes it.
  const rank = new Map<string, number>();
  const rankOf = (id: string) => {
    const r = rank.get(id);
    if (r === undefined) throw new Error(`Workflow step input "${id}" is not an earlier step`);
    return r;
  };
  for (const id of nodes.keys()) {
    const inputs = preds.get(id) ?? [];
    rank.set(id, inputs.length ? Math.max(...inputs.map(rankOf)) + 1 : 0);
  }

  const rows: string[][] = [];
  for (const [id, r] of rank) (rows[r] ??= []).push(id);

  const order = new Map<string, number>();
  rows.forEach((row, r) => {
    if (r > 0) {
      const mean = (id: string) => {
        const positions = (preds.get(id) ?? []).map((p) => order.get(p) ?? 0);
        return positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : 0;
      };
      row.sort((a, b) => mean(a) - mean(b));
    }
    row.forEach((id, i) => order.set(id, i));
  });

  const widest = Math.max(1, ...rows.map((row) => row.length));
  const width = widest * NODE_WIDTH + (widest - 1) * GAP_X;
  const heightOf = (kind: LineageNodeKind) =>
    kind === "query" || kind === "code" ? PILL_HEIGHT : BOX_HEIGHT;

  const placed: LineageNode[] = [];
  let top = 0;
  for (const row of rows) {
    const rowHeight = Math.max(...row.map((id) => heightOf(nodes.get(id)!.kind)));
    const rowWidth = row.length * NODE_WIDTH + (row.length - 1) * GAP_X;
    const left = (width - rowWidth) / 2;
    row.forEach((id, i) => {
      const node = nodes.get(id)!;
      const height = heightOf(node.kind);
      placed.push({
        ...node,
        x: left + i * (NODE_WIDTH + GAP_X),
        y: top + (rowHeight - height) / 2,
        width: NODE_WIDTH,
        height,
      });
    });
    top += rowHeight + GAP_Y;
  }

  const at = new Map(placed.map((n) => [n.id, n]));
  return {
    nodes: placed,
    edges: edges.map(({ from, to }) => ({ from, to, path: edgePath(at.get(from)!, at.get(to)!) })),
    width,
    height: Math.max(0, top - GAP_Y),
  };
}

function edgePath(from: LineageNode, to: LineageNode): string {
  const x1 = from.x + from.width / 2;
  const y1 = from.y + from.height;
  const x2 = to.x + to.width / 2;
  // Stop short of the node so the arrowhead sits on its border.
  const y2 = to.y - 2;
  if (x1 === x2) return `M${x1} ${y1} L${x2} ${y2}`;
  const mid = (y1 + y2) / 2;
  return `M${x1} ${y1} C${x1} ${mid} ${x2} ${mid} ${x2} ${y2}`;
}
